import { useEffect, useMemo } from 'react';
import { ThemeProvider } from '@mui/material';
import { createTheme, darken } from '@mui/material/styles';
import { theme as baseTheme, themeOptions } from '@/theme';
import { useEntityStore } from '@/store/entity';
import { useActiveBranding } from '@/api/settings';
import type { CachedBranding } from '@/lib/branding-cache';
import { fontStack, loadBrandFont } from '@/lib/brand-fonts';

/** The size body text is designed at; a branded font size scales every text
 *  style by size / this, so headings keep their proportion to the body. */
const BASE_FONT_SIZE = 13;

/** A contained button in `color`, darkened on hover. */
function containedIn(color: string) {
  let hover = color;
  try { hover = darken(color, 0.15); } catch { /* invalid hex → no hover shift */ }
  return { backgroundColor: color, '&:hover': { backgroundColor: hover } };
}

/** The base typography with the branded font, size and text colour. */
function brandTypography(b: CachedBranding) {
  const typo: Record<string, unknown> = { ...(themeOptions.typography as Record<string, unknown>) };
  const factor = b.font_size ? b.font_size / BASE_FONT_SIZE : 1;
  for (const [key, value] of Object.entries(typo)) {
    if (!value || typeof value !== 'object') continue;
    const style = { ...(value as Record<string, unknown>) };
    if (typeof style.fontSize === 'number') style.fontSize = Math.round(style.fontSize * factor * 10) / 10;
    if (b.text_color && style.color) style.color = b.text_color;
    typo[key] = style;
  }
  if (b.font_size) typo.fontSize = b.font_size;
  const stack = b.font_family ? fontStack(b.font_family) : undefined;
  if (stack) typo.fontFamily = stack;
  return typo;
}

/**
 * Wraps the app in a MUI ThemeProvider built from the active entity's branding:
 * `primary_color` becomes `primary.main`, and the button colours, text and link
 * colours, font and font size (set in Settings → Branding, or synced from the
 * uKnowva theme) override the rest. When the user switches entity in the
 * Topbar, the branding query re-fires and the theme rebuilds — buttons, tabs,
 * focus rings and type re-skin instantly.
 *
 * Falls back to the static `baseTheme` whenever:
 *   • no entity is active yet,
 *   • the branding row is empty / null,
 *   • the API call is in flight (avoids a flash of default-blue then re-tint).
 */
export function BrandedThemeProvider({ children }: { children: React.ReactNode }) {
  const entityId = useEntityStore((s) => s.entityId);
  const branding = useActiveBranding(entityId);

  useEffect(() => {
    if (branding?.font_family) loadBrandFont(branding.font_family);
  }, [branding?.font_family]);

  // The browser bar colour on phones: index.html's own value unless branded.
  useEffect(() => {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) return;
    meta.dataset.original ??= meta.content;
    meta.content = branding?.mobile_theme_color || meta.dataset.original;
  }, [branding?.mobile_theme_color]);

  // Form.io (dynamic collection forms) is styled via CSS, not the MUI theme,
  // so it can't read the palette. Publish the active brand/button colour as a
  // CSS variable its stylesheet consumes, so those forms follow branding too.
  useEffect(() => {
    const root = document.documentElement;
    const brand = branding?.primary_button_color || branding?.primary_color
      || baseTheme.palette.primary.main;
    root.style.setProperty('--ukv-brand', brand);
    try { root.style.setProperty('--ukv-brand-dark', darken(brand, 0.15)); }
    catch { root.style.setProperty('--ukv-brand-dark', brand); }
  }, [branding?.primary_button_color, branding?.primary_color]);

  const theme = useMemo(() => {
    const b: CachedBranding = branding ?? { primary_color: null, primary_button_color: null, logo_b64: null };
    const color = b.primary_color;
    // Button colour is independent of the brand colour; blank = fall back to it.
    const btnColor = b.primary_button_color;
    const secondaryBtn = b.secondary_button_color;
    const typed = b.font_family || b.font_size || b.text_color;
    if (!color && !btnColor && !secondaryBtn && !typed && !b.link_color) return baseTheme;

    // Only override contained buttons when a dedicated button colour is set;
    // otherwise primary buttons keep inheriting palette.primary.main (brand color).
    const buttonOverrides = {
      ...(btnColor ? { containedPrimary: containedIn(btnColor) } : {}),
      ...(secondaryBtn ? { containedSecondary: containedIn(secondaryBtn) } : {}),
    };

    // Build a derived theme that overrides only what branding touches.
    return createTheme({
      ...themeOptions,
      palette: {
        ...themeOptions.palette,
        primary: {
          ...baseTheme.palette.primary,
          main: color || baseTheme.palette.primary.main,
          // Light/dark are derived by MUI from `main` if not specified;
          // we drop the explicit ones so the picked color rules.
          light: undefined as unknown as string,
          dark:  undefined as unknown as string,
        },
        text: {
          ...baseTheme.palette.text,
          primary: b.text_color || baseTheme.palette.text.primary,
        },
      },
      typography: typed ? brandTypography(b) : themeOptions.typography,
      components: {
        ...themeOptions.components,
        MuiButton: {
          ...themeOptions.components?.MuiButton,
          styleOverrides: {
            ...themeOptions.components?.MuiButton?.styleOverrides,
            ...buttonOverrides,
          },
        },
        ...(b.link_color ? { MuiLink: { defaultProps: { color: b.link_color } } } : {}),
      },
    });
  }, [branding]);

  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
